import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseFormLeaveConfirmationProps {
  hasUnsavedChanges: boolean;
  onBeforeLeave?: () => void;
}

export const useFormLeaveConfirmation = ({ 
  hasUnsavedChanges, 
  onBeforeLeave 
}: UseFormLeaveConfirmationProps) => {
  const navigate = useNavigate();
  const isNavigatingRef = useRef(false);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  // Update the ref when hasUnsavedChanges changes
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip confirmation when a service worker update forces a reload
      if (window.__swUpdating) return;

      if (hasUnsavedChangesRef.current && !isNavigatingRef.current) {
        e.preventDefault();
        e.returnValue = 'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt verlaten?';
        return e.returnValue;
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChangesRef.current && !isNavigatingRef.current) {
        e.preventDefault();
        const confirmed = window.confirm(
          'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt verlaten?'
        );
        if (confirmed) {
          isNavigatingRef.current = true;
          onBeforeLeave?.();
          // Allow the navigation to proceed naturally
        } else {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    // Always add both listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, onBeforeLeave]);

  // Function to safely navigate (shows confirmation if there are unsaved changes)
  const safeNavigate = (path: string) => {
    if (hasUnsavedChangesRef.current && !isNavigatingRef.current) {
      const confirmed = window.confirm(
        'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt verlaten?'
      );
      if (confirmed) {
        isNavigatingRef.current = true;
        onBeforeLeave?.();
        navigate(path);
      }
    } else {
      isNavigatingRef.current = true;
      navigate(path);
    }
  };

  return { safeNavigate };
};
